import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { PencilLine, Save, Loader2, Eye } from "lucide-react";

const SAMPLE = {
  brand: "ISMO-TECH",
  order_number: "IT260906048956",
  amount: "12 000 FCFA",
  product: "Pack de Templates Photoshop Pro",
  process_link: "https://ismo.tech/admin/commandes/abc123",
  site_link: "https://ismo.tech",
};

function applyTokens(text, t) {
  let out = text || "";
  Object.entries(t).forEach(([k, v]) => { out = out.split(`{${k}}`).join(v); });
  return out;
}

function buildPreview(tpl) {
  const parts = [applyTokens(tpl.header, SAMPLE).trim(), applyTokens(tpl.body, SAMPLE).trim()];
  if (tpl.show_process_link) {
    const lbl = (applyTokens(tpl.process_label, SAMPLE) || "👉 Traiter la commande :").trim();
    parts.push(`${lbl}\n${SAMPLE.process_link}`);
  }
  if (tpl.show_site_link) {
    const lbl = (applyTokens(tpl.site_label, SAMPLE) || "🌐 Boutique :").trim();
    parts.push(`${lbl} ${SAMPLE.site_link}`);
  }
  if (tpl.security) parts.push(applyTokens(tpl.security, SAMPLE).trim());
  return parts.filter(Boolean).join("\n\n");
}

const field = "mt-1 w-full rounded-xl border border-line bg-white px-3 py-2 text-sm text-ink focus:border-brand focus:outline-none";
const lbl = "text-[13px] font-bold text-ink";

function Toggle({ checked, onChange, label, testId }) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-line bg-white px-3.5 py-2.5">
      <span className="text-[13px] font-semibold text-ink">{label}</span>
      <button type="button" role="switch" aria-checked={checked} data-testid={testId}
        onClick={() => onChange(!checked)}
        className={`relative h-6 w-11 shrink-0 rounded-full transition ${checked ? "bg-brand" : "bg-slate-300"}`}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${checked ? "left-[22px]" : "left-0.5"}`} />
      </button>
    </label>
  );
}

export default function AdminWhatsAppMessage() {
  const [tpl, setTpl] = useState(null);
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/whatsapp-template")
      .then(({ data }) => { setTpl(data.template); setPlaceholders(data.placeholders || []); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setTpl((p) => ({ ...p, [k]: v }));
  const preview = useMemo(() => (tpl ? buildPreview(tpl) : ""), [tpl]);

  const save = async () => {
    setBusy(true);
    try {
      const { key, updated_at, ...body } = tpl;
      await api.put("/admin/whatsapp-template", body);
      toast.success("Message WhatsApp enregistré ✅");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (loading || !tpl) return <div className="p-6 text-slate-500">Chargement…</div>;

  return (
    <div data-testid="admin-wa-message-page" className="max-w-5xl">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-ink text-white">
          <PencilLine className="h-5 w-5" />
        </span>
        <div>
          <h1 className="font-display text-2xl font-extrabold text-ink">Message WhatsApp (nouvelle commande)</h1>
          <p className="mt-0.5 text-sm text-slate-500">Personnalisez le texte de notification. Le nom de la boutique reste dynamique via <code>{"{brand}"}</code> (Admin → ISMO-TECH, revendeur → sa boutique).</p>
        </div>
      </div>

      <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4">
        <Toggle checked={tpl.enabled} onChange={(v) => set("enabled", v)} label="Utiliser mon message personnalisé (sinon, le modèle par défaut du site est conservé)" testId="wa-tpl-enabled" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4 rounded-2xl border border-line bg-white p-5">
          <div>
            <span className={lbl}>En-tête</span>
            <input className={field} value={tpl.header} onChange={(e) => set("header", e.target.value)} data-testid="wa-tpl-header" />
          </div>
          <div>
            <span className={lbl}>Corps du message</span>
            <textarea rows={6} className={field} value={tpl.body} onChange={(e) => set("body", e.target.value)} data-testid="wa-tpl-body" />
          </div>
          <div>
            <span className={lbl}>Message de sécurité</span>
            <textarea rows={2} className={field} value={tpl.security} onChange={(e) => set("security", e.target.value)} data-testid="wa-tpl-security" />
          </div>

          <div className="grid gap-2.5">
            <Toggle checked={tpl.show_process_link} onChange={(v) => set("show_process_link", v)} label="Afficher le lien « Traiter la commande »" testId="wa-tpl-show-process" />
            {tpl.show_process_link && (
              <input className={field} value={tpl.process_label} onChange={(e) => set("process_label", e.target.value)} placeholder="👉 Traiter la commande :" data-testid="wa-tpl-process-label" />
            )}
            <Toggle checked={tpl.show_site_link} onChange={(v) => set("show_site_link", v)} label="Afficher le lien du site" testId="wa-tpl-show-site" />
            {tpl.show_site_link && (
              <input className={field} value={tpl.site_label} onChange={(e) => set("site_label", e.target.value)} placeholder="🌐 Boutique :" data-testid="wa-tpl-site-label" />
            )}
            <Toggle checked={tpl.show_product_image} onChange={(v) => set("show_product_image", v)} label="Joindre l'image du produit" testId="wa-tpl-show-image" />
          </div>

          <div className="rounded-xl bg-slate-50 p-3 text-[12px] text-slate-500">
            <p className="mb-1 font-bold text-slate-600">Variables disponibles :</p>
            <div className="flex flex-wrap gap-1.5">
              {placeholders.map((p) => (
                <code key={p} className="rounded bg-white px-1.5 py-0.5 text-[11px] font-bold text-brand ring-1 ring-line">{p}</code>
              ))}
            </div>
          </div>

          <button onClick={save} disabled={busy} data-testid="wa-tpl-save"
            className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-bold text-white transition hover:bg-black disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
          </button>
        </div>

        <div>
          <div className="mb-2 flex items-center gap-2 text-sm font-bold text-ink"><Eye className="h-4 w-4" /> Aperçu WhatsApp</div>
          <div className="rounded-2xl bg-[#0b141a] p-4">
            <div className="ml-auto max-w-[92%] rounded-lg rounded-tr-none bg-[#005c4b] p-3 text-[13.5px] leading-relaxed text-white shadow" data-testid="wa-tpl-preview">
              {tpl.show_product_image && (
                <div className="mb-2 flex h-24 items-center justify-center rounded-md bg-white/10 text-[11px] text-white/70">🖼️ Image du produit</div>
              )}
              <pre className="whitespace-pre-wrap break-words font-sans">{preview}</pre>
              <span className="mt-1 block text-right text-[10px] text-white/60">12:24 ✓✓</span>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-slate-400">Aperçu avec des données d'exemple. L'image du produit s'affiche dans la conversation active du destinataire.</p>
        </div>
      </div>
    </div>
  );
}
